import { motion } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import { Reveal, Overline } from "./Reveal";

const img = (id, w = 1100) => `https://images.unsplash.com/${id}?auto=format&fit=crop&q=80&w=${w}`;

const COLLAGE = [
  { src: img("photo-1531834685032-c34bf0d84c77"), caption: "Foundation Work", city: "Bardstown", ratio: "aspect-[4/5]" },
  { src: img("photo-1628744448839-170bf324f27e"), caption: "Driveway Replacement", city: "Mount Washington", ratio: "aspect-[4/3]" },
  { src: img("photo-1621922688758-359fc864071e"), caption: "Exterior Excavation", city: "Shepherdsville", ratio: "aspect-[4/5]" },
  { src: img("photo-1600573472592-401b489a3cdc"), caption: "Custom Patio", city: "Elizabethtown", ratio: "aspect-[4/3]" },
  { src: img("photo-1508450859948-4e04fabaa4ea"), caption: "Commercial Project", city: "Lexington", ratio: "aspect-[4/5]" },
  { src: img("photo-1567954970774-58d6aa6c50dc"), caption: "Crew On Site", city: "Louisville", ratio: "aspect-[4/3]" },
];

const Card = ({ item, delay }) => (
  <motion.figure
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className="group relative overflow-hidden rounded-2xl border border-white/10"
  >
    <img
      src={item.src}
      alt={`${item.caption} — ${item.city}, KY`}
      loading="lazy"
      className={`w-full ${item.ratio} object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]`}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
    <figcaption className="absolute bottom-0 inset-x-0 p-5">
      <span className="block h-[2px] w-8 bg-gradient-to-r from-[#00E5FF] to-[#0066FF] mb-3 transition-[width] duration-500 group-hover:w-16" />
      <span className="font-display text-lg font-semibold text-white">{item.caption}</span>
      <span className="flex items-center gap-1.5 text-xs text-gray-400 mt-0.5">
        <MapPin size={12} className="text-cyan-400" /> {item.city}, KY
      </span>
    </figcaption>
  </motion.figure>
);

export default function ResultsCollage() {
  return (
    <section id="results" data-testid="results-section" className="bg-[#0A0A0A] py-28 md:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Reveal>
          <Overline>Proof</Overline>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
              Real Problems. Real Results.
            </h2>
            <p className="max-w-md text-gray-400">
              Crews, equipment, and finished work from jobs across Kentucky — the work speaks for itself.
            </p>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="grid gap-6">
            <Card item={COLLAGE[0]} delay={0} />
            <Card item={COLLAGE[1]} delay={0.08} />
          </div>
          <div className="grid gap-6 lg:translate-y-12">
            <Card item={COLLAGE[2]} delay={0.05} />
            <Card item={COLLAGE[3]} delay={0.13} />
          </div>
          <div className="grid gap-6">
            <Card item={COLLAGE[4]} delay={0.1} />
            <Card item={COLLAGE[5]} delay={0.18} />
          </div>
        </div>

        <Reveal className="mt-16 lg:mt-24 text-center">
          <p className="text-xs text-gray-600 mb-5">
            Representative project imagery — real Phillips job photos are being added from the field library.
          </p>
          <a
            href="#projects"
            data-testid="results-view-projects-btn"
            className="inline-flex items-center gap-2 rounded-md bg-[#0066FF] px-8 py-4 font-display text-sm font-semibold tracking-widest text-white transition-[background-color,box-shadow,color] duration-300 hover:bg-[#00E5FF] hover:text-black hover:shadow-[0_0_30px_rgba(0,229,255,0.5)]"
          >
            SEE FULL PROJECT GALLERY <ArrowRight size={16} />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
